import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import { formatDay } from "../format";
import {
  incidentPeriodDays,
  type IncidentPeriodSummary,
  type IncidentSummaryPeriod,
  type IncidentSummaryResponse,
} from "@visioni/a3s-monitoring/app";

type Counts = IncidentSummaryResponse["totals"];

/**
 * The headline figures, in the order support reads them: what operators acted
 * on, then the activations, so they compare with one another ("15 activations,
 * 10 in 3 genuine incidents, 5 non-genuine").
 */
const HEADLINES = [
  { key: "genuineIncidents", label: "Genuine incidents" },
  { key: "policeIncidents", label: "Police incidents" },
  { key: "activationsInGenuineIncidents", label: "Activations in genuine incidents" },
  { key: "nonGenuineActivations", label: "Non-genuine activations" },
] as const satisfies readonly { key: keyof Counts; label: string }[];

const PROVISIONAL =
  "Provisional: some incidents or activations counted here have not settled, so these figures may still change.";

/**
 * Headline totals for the chosen window, then the same counts per day, week or
 * month. A period with nothing counted is absent rather than zero.
 *
 * Each period's row links to the list of its incidents. The activation counts
 * have no list of their own: an activation's classification is counted but not
 * stored, so no list can show just the ones a cell counts.
 */
export function IncidentTotals({
  summary,
  period,
  isStale,
  incidentsHrefFor,
}: {
  readonly summary: IncidentSummaryResponse;
  /** What the summary's rows are: days, weeks or months. */
  readonly period: IncidentSummaryPeriod;
  readonly isStale: boolean;
  /** The incident list for one period, by its label. */
  readonly incidentsHrefFor: (period: string) => Href;
}) {
  const { totals, periods } = summary;
  return (
    <View style={[styles.wrapper, isStale && styles.stale]}>
      <View style={styles.tiles}>
        {HEADLINES.map(({ key, label }) => (
          <View key={key} style={styles.tile}>
            <Text textStyle="xs/semibold" color="textMuted">
              {label}
            </Text>
            <Text textStyle="lg/bold" color="text">
              {totals[key].toLocaleString("en-AU")}
            </Text>
          </View>
        ))}
      </View>
      {!totals.settled && (
        <Text textStyle="xs/regular" color="textMuted">
          {PROVISIONAL}
        </Text>
      )}
      {periods.length > 0 && <PeriodTable periods={periods} period={period} incidentsHrefFor={incidentsHrefFor} />}
    </View>
  );
}

function PeriodTable({
  periods,
  period: kind,
  incidentsHrefFor,
}: {
  readonly periods: readonly IncidentPeriodSummary[];
  readonly period: IncidentSummaryPeriod;
  readonly incidentsHrefFor: (period: string) => Href;
}) {
  return (
    <View style={styles.card}>
      <View style={[styles.row, styles.headerRow]}>
        <Text textStyle="xs/semibold" color="textMuted" style={styles.period}>
          Period
        </Text>
        {HEADLINES.map(({ key, label }) => (
          <Text key={key} textStyle="xs/semibold" color="textMuted" style={styles.count}>
            {label}
          </Text>
        ))}
      </View>
      {periods.map((period) => (
        <TenantLink key={period.period} href={incidentsHrefFor(period.period)} style={styles.link}>
          {({ hovered }) => (
            <View style={[styles.row, hovered && styles.hovered]}>
              <Text textStyle="xs/regular" color="text" style={styles.period}>
                {period.settled ?
                  periodLabel(kind, period.period)
                : `${periodLabel(kind, period.period)} (provisional)`}
              </Text>
              {HEADLINES.map(({ key }) => (
                <Text key={key} textStyle="xs/regular" color="text" style={styles.count}>
                  {period[key].toLocaleString("en-AU")}
                </Text>
              ))}
            </View>
          )}
        </TenantLink>
      ))}
    </View>
  );
}

/** A row's period as its day, or its first and last day for a week or month. */
function periodLabel(period: IncidentSummaryPeriod, label: string): string {
  const { first, last } = incidentPeriodDays({ period, label });
  return first === last ? formatDay(first) : `${formatDay(first)} to ${formatDay(last)}`;
}

const styles = StyleSheet.create((theme) => ({
  wrapper: { gap: theme.spacing.lg },
  stale: { opacity: 0.5 },
  tiles: { flexDirection: "row", flexWrap: "wrap", gap: theme.spacing.lg },
  tile: {
    flexBasis: "22%",
    flexGrow: 1,
    minWidth: 160,
    gap: theme.spacing.xs,
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
  },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.md,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  link: { cursor: "pointer" },
  hovered: { backgroundColor: theme.colors.surfaceCardInner },
  period: { width: 260 },
  count: { flex: 1, minWidth: 100, textAlign: "right" },
}));
